import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { pageview } from "@/config/analytics";
import { LanguageProvider } from "@/contexts/language/LanguageContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import EcommerceAutomation from "./pages/EcommerceAutomation";
import SasMigrations from "./pages/SasMigrations";
import Content from "./pages/Content";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import CaseStudies from "./pages/CaseStudies";
import Papers from "./pages/Papers";
import ProductManagement from "./pages/ProductManagement";
import TalkToYourData from "./pages/TalkToYourData";
import DataPlatform from "./pages/DataPlatform";
import About from "./pages/About";
import Solutions from "./pages/Solutions";
import IntegratedRetailMasterDataModel from "./pages/IntegratedRetailMasterDataModel";
import RetailDataWorkshop from "./pages/RetailDataWorkshop";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Careers from "./pages/Careers";
import JobDetail from "./pages/JobDetail";
import OurWorkData from "./pages/OurWorkData";
import OurWorkRetailManagement from "./pages/OurWorkRetailManagement";
import OurWorkContextEngineering from "./pages/OurWorkContextEngineering";
import OurWorkDataEngineering from "./pages/OurWorkDataEngineering";
import OurWorkDigitalCFO from "./pages/OurWorkDigitalCFO";
import HowItWorks from "./pages/HowItWorks";
import CaseStudyAlasans from "./pages/CaseStudyAlasans";
import CaseStudyDCM from "./pages/CaseStudyDCM";
import CaseStudyHeroHero from "./pages/CaseStudyHeroHero";
import CaseStudyAltisport from "./pages/CaseStudyAltisport";
import TellMeMore from "./pages/TellMeMore";
import BookACall from "./pages/BookACall";
import StayInTouch from "./pages/StayInTouch";
import Webinar from "./pages/Webinar";
import WebinarThanks from "./pages/WebinarThanks";

// Scroll to top on route change (page views handled by GTM All Pages trigger)
const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) return; // let the target page's own hash-scroll effect handle it
    window.scrollTo(0, 0);
  }, [location]);

  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Language comes from the URL (/sk/...), so the provider sits inside the router */}
        <LanguageProvider>
          {/* Add the tracker component */}
          <RouteChangeTracker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services/ecommerce-automation" element={<EcommerceAutomation />} />
            <Route path="/solutions/retail-automation-suite" element={<Solutions />} />
            <Route path="/solutions/integrated-retail-master-data-model" element={<IntegratedRetailMasterDataModel />} />
            <Route path="/solutions/retail-data-workshop" element={<RetailDataWorkshop />} />
            <Route path="/services/ecommerce-automation/product-management" element={<ProductManagement />} />
            <Route path="/services/sas-migrations" element={<SasMigrations />} />
            <Route path="/services/data-platform" element={<DataPlatform />} />
            <Route path="/talk-to-your-data" element={<TalkToYourData />} />
            <Route path="/content" element={<Content />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/papers" element={<Papers />} />
            <Route path="/about" element={<About />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<JobDetail />} />
            <Route path="/our-work/data" element={<OurWorkData />} />
            <Route path="/our-work/retail-profit-management" element={<OurWorkRetailManagement />} />
            <Route path="/our-work/context-engineering" element={<OurWorkContextEngineering />} />
            <Route path="/our-work/data-engineering" element={<OurWorkDataEngineering />} />
            <Route path="/our-work/digital-cfo" element={<OurWorkDigitalCFO />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/case-studies/alasans" element={<CaseStudyAlasans />} />
            <Route path="/case-studies/dcm" element={<CaseStudyDCM />} />
            <Route path="/case-studies/herohero" element={<CaseStudyHeroHero />} />
            <Route path="/case-studies/altisport" element={<CaseStudyAltisport />} />
            <Route path="/tell-me-more" element={<TellMeMore />} />
            <Route path="/book-a-call" element={<BookACall />} />
            <Route path="/stay-in-touch" element={<StayInTouch />} />
            <Route path="/webinar" element={<Webinar />} />
            <Route path="/webinar/dakujeme" element={<WebinarThanks />} />
            {/* Slovak versions — keep in sync with SK_PATHS in src/contexts/language/routing.ts */}
            <Route path="/sk" element={<Index />} />
            <Route path="/sk/about" element={<About />} />
            <Route path="/sk/content" element={<Content />} />
            <Route path="/sk/how-it-works" element={<HowItWorks />} />
            <Route path="/sk/solutions" element={<Solutions />} />
            <Route path="/sk/solutions/retail-automation-suite" element={<Solutions />} />
            <Route path="/sk/our-work/data" element={<OurWorkData />} />
            <Route path="/sk/our-work/digital-cfo" element={<OurWorkDigitalCFO />} />
            <Route path="/sk/our-work/retail-profit-management" element={<OurWorkRetailManagement />} />
            <Route path="/sk/our-work/data-engineering" element={<OurWorkDataEngineering />} />
            <Route path="/sk/our-work/context-engineering" element={<OurWorkContextEngineering />} />
            <Route path="/sk/tell-me-more" element={<TellMeMore />} />
            <Route path="/sk/book-a-call" element={<BookACall />} />
            <Route path="/sk/case-studies/altisport" element={<CaseStudyAltisport />} />
            <Route path="/sk/case-studies/herohero" element={<CaseStudyHeroHero />} />
            <Route path="/sk/case-studies/alasans" element={<CaseStudyAlasans />} />
            <Route path="/sk/case-studies/dcm" element={<CaseStudyDCM />} />
            <Route path="/sk/blog/:slug" element={<BlogPost />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
